/**
 * Ted #32a (E1.1, #1156) — the ONE "current coverage" rule for a dog's
 * vaccinations on every desk surface.
 *
 * THE BUG THIS CLOSES. A dog migrated from Gingr carries every version of
 * every shot: the sync mints a new row each time Gingr touches a record
 * (E1.2 fixes that), so a dog with a normal history has, say, three lapsed
 * Bordetella rows beside the current one. Every desk surface asked "is ANY
 * row expired?" and so read that dog as `Vax expired` — the check-in
 * interrupt (#1120) fired for a compliant dog, the kennel roster and the
 * compliance page listed it, and the customer app (which goes through the
 * booking gate's latest-record rule) never did. The desk and the gate
 * disagreed about the same dog.
 *
 * THE RULE. A dog's coverage for a vaccine is decided by its LATEST record
 * for that vaccine, and only that record:
 *
 *   1. the record with the latest expiry date wins;
 *   2. on an equal expiry, the latest date given wins;
 *   3. on an equal expiry and date given, a staff-verified record wins
 *      (two copies of the same shot: the signed-off one is the answer);
 *   4. otherwise the first in input order (deterministic, never random).
 *
 * A record with NO expiry date ranks below any record that has one. That is
 * the conservative direction and the reason it is safe to ship: compared
 * with the old "any row" rule, this can only ever REMOVE an alert when a
 * later-dated record for the same vaccine exists. A lapsed row next to an
 * undated one still reads lapsed, exactly as before; a dog whose only record
 * is undated still reads present-and-never-alerting, exactly as before.
 *
 * Vaccines are grouped case- and whitespace-insensitively ("Rabies" from
 * Gingr and "rabies" from intake are one vaccine). Different names stay
 * different vaccines: Gingr's `DHLPP` and intake's `dhpp` are NOT merged
 * here — that is import normalization (E1.2), not a status rule.
 *
 * WHAT IS DELIBERATELY NOT HERE: thresholds (30-day "expiring", grace
 * windows), "today", and verification policy. Each surface keeps its own
 * question; this module only answers "which of these rows speaks for the
 * vaccine?". The booking gate (`vaccination-compliance.ts`) still selects by
 * date given and is unchanged by this PR (#1156 keeps booking create out of
 * scope); for a Gingr dog its latest-touched record is the renewal, so the
 * two agree on the cases Ted reported — see the agreement test.
 *
 * Pure: no I/O, no Prisma, no clock.
 */

/** The fields the rule reads. Callers map their row shape onto this. */
export interface CoverageRecord {
  readonly name: string;
  /** `@db.Date`: a Date (Prisma), an ISO string (raw SQL / JSON), or null. */
  readonly expiresAt: Date | string | null;
  /** `@db.Date`. Optional: a read that did not select it ranks by expiry alone. */
  readonly dateGiven?: Date | string | null;
  readonly verified?: boolean | null;
}

/**
 * A `@db.Date` value as the UTC day-start in ms, or null when absent or
 * unparseable. `@db.Date` is a calendar day stored as UTC midnight, so the
 * comparison is day-granular in UTC — never the server's or the browser's
 * local day, which would move a date across midnight.
 */
export function calendarDayMs(value: Date | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    if (Number.isNaN(t)) return null;
    return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  }
  // A bare 'YYYY-MM-DD' or a full timestamp: the calendar day is the prefix.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (dateOnly) {
    return Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

/** The grouping key: one vaccine regardless of case or stray spaces. */
export function vaccineKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Missing dates rank below every real date. */
function rankDay(value: Date | string | null | undefined): number {
  return calendarDayMs(value) ?? Number.NEGATIVE_INFINITY;
}

/**
 * Comparator: negative when `a` is the LATER record (so it sorts first).
 * Exported for callers that need the records of one vaccine in rule order.
 */
export function compareLatestFirst(a: CoverageRecord, b: CoverageRecord): number {
  const byExpiry = rankDay(b.expiresAt) - rankDay(a.expiresAt);
  if (byExpiry !== 0 && !Number.isNaN(byExpiry)) return byExpiry;
  const byGiven = rankDay(b.dateGiven) - rankDay(a.dateGiven);
  if (byGiven !== 0 && !Number.isNaN(byGiven)) return byGiven;
  const av = a.verified === true ? 1 : 0;
  const bv = b.verified === true ? 1 : 0;
  return bv - av;
}

/**
 * The records that currently speak for the dog: exactly one per vaccine,
 * returned as the SAME object references the caller passed (so a caller can
 * mark the rest as superseded with a Set), in the order each vaccine first
 * appears in the input.
 */
export function currentCoverage<T extends CoverageRecord>(records: ReadonlyArray<T>): T[] {
  const winners = new Map<string, T>();
  for (const r of records) {
    const key = vaccineKey(r.name);
    const held = winners.get(key);
    // Strictly later only: on a full tie the earlier input row keeps its place.
    if (held === undefined || compareLatestFirst(r, held) < 0) winners.set(key, r);
  }
  return [...winners.values()];
}

/**
 * True for a record that an equal-or-later record of the same vaccine
 * replaces. Built once per dog; look records up by reference.
 */
export function supersededRecords<T extends CoverageRecord>(
  records: ReadonlyArray<T>,
): ReadonlySet<T> {
  const current = new Set<T>(currentCoverage(records));
  return new Set(records.filter((r) => !current.has(r)));
}
